import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Clock, Users, ArrowRight, Sparkles, ShieldCheck, Star } from "lucide-react";
import { getPoojas } from "../lib/api";

const CATS = ["All", "Home", "Festival", "Shanti", "Family"];
const HERO_IMG = "https://images.unsplash.com/photo-1622036160677-f29a79c4dd74?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Home() {
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [poojas, setPoojas] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    setLoading(true);
    getPoojas(cat).then((d) => setPoojas(d.poojas)).finally(() => setLoading(false));
  }, [cat]);

  const list = poojas.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="cust-home" className="pc-fade-in">
      {/* hero */}
      <section className="px-4 pt-4 sm:pt-6">
        <div className="relative overflow-hidden rounded-3xl border border-[#8B1A2B]/10 bg-[#2A0E13] shadow-[0_30px_60px_-30px_rgba(139,26,43,0.6)]">
          <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-tr from-[#3B0D14] via-[#6E1321]/70 to-transparent" />
          <div className="relative px-6 py-10 sm:px-10 sm:py-14 lg:py-20">
            <p className="pc-fade-up text-[11px] font-semibold uppercase tracking-[0.2em] text-[#F2C77E]">Serving Rajajinagar · Bengaluru</p>
            <h1 className="pc-fade-up mt-2 max-w-2xl font-heading text-4xl leading-[1.05] text-white sm:text-5xl lg:text-6xl" style={{ animationDelay: ".05s" }}>A ceremony,<br />handled beautifully.</h1>
            <p className="pc-fade-up mt-3 max-w-lg text-sm text-white/85 sm:text-base" style={{ animationDelay: ".1s" }}>Discover KYC-verified purohiths or invite proposals with one clear request — matched to your tradition and language.</p>
            <div className="pc-fade-up mt-6 flex max-w-xl items-center gap-2 rounded-2xl border border-white/20 bg-white/95 px-4 py-3 shadow-lg backdrop-blur" style={{ animationDelay: ".15s" }}>
              <Search size={18} className="text-[#8B1A2B]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="cust-search" placeholder="Search poojas and ceremonies"
                className="w-full bg-transparent text-sm text-[#2A211C] outline-none placeholder:text-[#A0968A]" />
              <button onClick={() => nav("/app/request")} data-testid="cust-hero-request" className="hidden shrink-0 rounded-xl bg-[#8B1A2B] px-4 py-2 text-sm font-semibold text-white transition active:scale-[0.98] hover:bg-[#6F1420] sm:block">Get proposals</button>
            </div>
            <div className="pc-fade-up mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-white/80" style={{ animationDelay: ".2s" }}>
              <span className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-[#8ED9A6]" /> Verified purohiths</span>
              <span className="flex items-center gap-1.5"><Star size={14} className="fill-[#F2C77E] text-[#F2C77E]" /> Rated by families</span>
              <span className="flex items-center gap-1.5"><Sparkles size={14} className="text-[#F2C77E]" /> Kannada-first</span>
            </div>
          </div>
        </div>
      </section>

      {/* categories */}
      <section className="px-4 pt-8">
        <div className="flex items-end justify-between">
          <h2 className="font-heading text-2xl text-[#2A211C]">Explore ceremonies</h2>
          <span className="hidden text-xs text-[#A0968A] sm:block">{list.length} available</span>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`cust-cat-${c.toLowerCase()}`}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition active:scale-[0.98] ${cat === c ? "bg-[#8B1A2B] text-white shadow-[0_6px_16px_-8px_rgba(139,26,43,0.8)]" : "border border-[#E7DCCB] bg-white text-[#5C5149] hover:border-[#8B1A2B]/40"}`}>
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* poojas grid */}
      <section className="px-4 pt-6">
        <div className="pc-stagger grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {loading && Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-72 animate-pulse rounded-2xl bg-[#F1E7D7]" />)}
          {!loading && list.map((p) => (
            <div key={p.id} data-testid={`cust-pooja-${p.slug}`} className="pc-card-hover group overflow-hidden rounded-2xl border border-[#EBE0D0] bg-white">
              <div className="relative h-44 w-full overflow-hidden bg-[#F1E7D7]">
                <img src={p.image_url} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <span className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm">
                  <Clock size={11} /> {p.duration_text}
                </span>
              </div>
              <div className="p-4">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-[#B58A5E]">Ceremony</p>
                <h3 className="mt-0.5 font-heading text-lg text-[#2A211C]">{p.name}</h3>
                <p className="mt-1 text-sm text-[#7A6E64]">Starting from <span className="font-semibold text-[#2A211C]">₹{p.price.toLocaleString("en-IN")}</span></p>
                <div className="mt-3 flex gap-2">
                  <Link to={`/app/priests?poojaSlug=${p.slug}&poojaName=${encodeURIComponent(p.name)}`} data-testid={`cust-view-purohits-${p.slug}`}
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-[#E0D2BF] bg-white py-2.5 text-sm font-semibold text-[#5C5149] transition hover:border-[#8B1A2B] hover:text-[#8B1A2B]">
                    <Users size={15} /> Purohits
                  </Link>
                  <Link to={`/app/request?poojaSlug=${p.slug}&poojaName=${encodeURIComponent(p.name)}`} data-testid={`cust-get-proposals-${p.slug}`}
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-[#8B1A2B] py-2.5 text-sm font-semibold text-white transition active:scale-[0.98] hover:bg-[#6F1420]">
                    Proposals <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA + PuroMitra */}
      <section className="grid gap-4 px-4 py-8 lg:grid-cols-2">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#8B1A2B] to-[#5C0F1A] p-6 text-white">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-white/70">Request proposals</p>
          <h3 className="mt-1 font-heading text-2xl">Invite the right purohit to your ceremony.</h3>
          <p className="mt-1 text-sm text-white/80">Share the details, compare verified offers, and choose comfortably.</p>
          <button onClick={() => nav("/app/request")} data-testid="cust-start-request"
            className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#8B1A2B] transition active:scale-[0.98] hover:bg-[#F5E9DA]">
            Start a request <ArrowRight size={15} />
          </button>
        </div>
        <Link to="/app/puromitra" data-testid="home-puromitra-card" className="pc-card-hover flex items-center gap-4 rounded-2xl border border-[#EBE0D0] bg-white p-6">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-[#F5E9DA] text-[#8B1A2B]"><Sparkles size={26} /></span>
          <div>
            <p className="font-heading text-xl text-[#2A211C]">PuroMitra · your pooja assistant</p>
            <p className="mt-1 text-sm text-[#7A6E64]">Ask anything about rituals, samagri and muhurta — in Kannada or English.</p>
            <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-[#8B1A2B]">Start chatting <ArrowRight size={14} /></span>
          </div>
        </Link>
      </section>
    </div>
  );
}
